/* =====================================================================
   ai-main-product-v2.js  — upload to Shopify Files
   ---------------------------------------------------------------------
   Recreates main-product-v2.js minus the old buy box logic. The
   ai-buybox component is the source of truth for variant + plan
   selection, ATC, and the cart-add. This file keeps:
     - Product image swiper + thumbnails
     - "X people are checking out" counter
     - Pre-order date + percent-sold updaters
     - Add-ons swiper
     - Sticky ATC display + click (wired to window.aiBuybox)
     - GA4 + Klaviyo "Added to Cart" tracking (fires off aiBuybox:added)

   Listens for `aiBuybox:change` / `aiBuybox:added` on window — both
   emitted by ai-buybox.js.
   ===================================================================== */
(async function () {
    // Locate the AI product section. The script tag passes the dynamic
    // Shopify section.id via data-id, but our section markup uses the
    // hardcoded wrapper id="aiProductV2" so both selectors win at different
    // times: data-id when the wrapper IS the shopify-section-* element,
    // #aiProductV2 when the wrapper is our inner element. Try data-id first
    // (matches the original main-product-v2.js convention), fall back to
    // the hardcoded id (matches the AI section's markup).
    const sectionID = (document.currentScript && document.currentScript.dataset.id) || '';
    const section =
        (sectionID && document.querySelector('#' + sectionID)) ||
        document.querySelector('#aiProductV2');

    if (!section) {
        console.warn('AI Product section not found (tried #' + sectionID + ' and #aiProductV2).');
        return;
    }

    const productJSON = section.querySelector('.templateProductJSON');
    const product = productJSON ? JSON.parse(productJSON.textContent) : null;
    let activeVariant = null;   // mirrors the buybox's current selection
    const quantity = 1;

    if (window.Shopify && Shopify.theme && Shopify.theme.role === "development") {
        window.product = product;
    }

    /* ============ Product image swiper ============ */
    let swiper = null;
    if (typeof Swiper !== "undefined") {
        swiper = new Swiper(section.querySelector('.productV2VisualSwiper'), {
            navigation: {
                nextEl: section.querySelector('.swiper-button-next'),
                prevEl: section.querySelector('.swiper-button-prev'),
            }
        });

        section.querySelectorAll('.productV2VisualThumbsThumbnail').forEach(function (el, index) {
            el.addEventListener('click', function () {
                swiper && swiper.slideTo(index);
            });
        });

        /* Add-ons swiper */
        const addOnEl = section.querySelector('.productV3AddOn');
        if (addOnEl) {
            new Swiper(addOnEl, {
                slidesPerView: 1,
                spaceBetween: 10,
                pagination: { el: section.querySelector('.productV3AddOnPag'), clickable: true }
            });
        }
    } else {
        console.warn('Swiper not available — image carousel/add-ons will be static.');
    }

    /* ============ Sticky ATC display ============ */
    const stickyAmount       = section.querySelector('.productV2StickyBodyRightOptionAmount');
    const stickyInterval     = section.querySelector('.productV2StickyBodyRightOptionInterval');
    const stickyMobileAmount = section.querySelector('.productV2StickyBodyLeftMobileAmount');
    const stickyMobileInt    = section.querySelector('.productV2StickyBodyLeftMobileInterval');
    const stickyCompare      = section.querySelector('.productV2StickyBodyRightATCCompare');
    const stickyPrice        = section.querySelector('.productV2StickyBodyRightATCPrice');
    const stickyBtn          = section.querySelector('.productV2StickyBodyRightATC');
    const stickyTxt          = section.querySelector('.productV2StickyBodyRightATCTxt');

    function moneyUSD(n) {
        if (typeof n !== 'number') return '';
        return '$' + n.toFixed(2);
    }

    function updateStickyFromState(state) {
        if (!state) return;
        activeVariant = state;

        // "Buy 2 + Get 1" style amount text — falls back to lead label.
        const amount = (state.card && (state.card.leadLabel || '')).toUpperCase();
        if (stickyAmount)       stickyAmount.textContent = amount;
        if (stickyMobileAmount) stickyMobileAmount.textContent = amount;

        // "ships every 3 months" interval — only show on subscribe.
        let interval = '';
        if (state.mode === 'subscribe' && state.card) {
            const ship = state.card.ship || '';
            const idx = ship.toLowerCase().indexOf('ships');
            if (idx > -1) interval = ship.slice(idx);
        }
        if (stickyInterval)  stickyInterval.textContent  = interval;
        if (stickyMobileInt) stickyMobileInt.textContent = interval;

        if (stickyCompare) {
            stickyCompare.textContent = (state.compareAtPrice && state.compareAtPrice > (state.price || 0))
                ? moneyUSD(state.compareAtPrice)
                : '';
        }
        if (stickyPrice) stickyPrice.textContent = moneyUSD(state.price);

        if (stickyTxt && stickyBtn) {
            if (product && product.metafields_present_sold_out_flag === true) { /* defensive */ }
            stickyTxt.textContent = 'ADD TO CART';
            stickyBtn.disabled = false;
        }
    }

    /* React to picker state changes */
    window.addEventListener('aiBuybox:change', function (e) { updateStickyFromState(e.detail); });

    /* Initial paint — buybox may have already initialised before this listener was added. */
    if (window.aiBuybox && typeof window.aiBuybox.getState === 'function') {
        updateStickyFromState(window.aiBuybox.getState());
    }

    /* Sticky ATC click → defer to the buybox's own cart-add */
    if (stickyBtn) {
        stickyBtn.addEventListener('click', async function () {
            stickyBtn.classList.add('loading');
            try {
                if (window.aiBuybox && typeof window.aiBuybox.addCurrentSelectionToCart === 'function') {
                    await window.aiBuybox.addCurrentSelectionToCart();
                }
            } catch (err) {
                console.error('Sticky ATC failed:', err);
            } finally {
                setTimeout(function () { stickyBtn.classList.remove('loading'); }, 600);
            }
        });
    }

    /* ============ GA4 + Klaviyo tracking ============ */
    window.addEventListener('aiBuybox:added', function (e) {
        const state = e.detail;
        if (!state || !product) return;

        const priceCents = Math.round((state.price || 0) * 100);

        try {
            if (typeof dataLayer !== 'undefined') {
                dataLayer.push({ ecommerce: null });
                dataLayer.push({
                    event: 'add_to_cart',
                    ecommerce: {
                        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
                        value: (state.price || 0) * quantity,
                        items: [{
                            item_id: product.id,
                            item_name: product.title,
                            item_category: product.type,
                            item_variant: state.cardId,
                            price: state.price || 0,
                            quantity: quantity,
                            subscription_item: !!state.sellingPlanId
                        }],
                        subscription_included: !!state.sellingPlanId
                    }
                });
            }
        } catch (gaErr) { console.warn('GA4 push failed:', gaErr); }

        try {
            if (typeof _learnq !== 'undefined') {
                _learnq.push(['track', 'Added to Cart', {
                    '$value': state.price || 0,
                    'AddedItemProductName': product.title,
                    'AddedItemProductID': product.id,
                    'AddedItemSKU': product.variants && product.variants[0] && product.variants[0].sku,
                    'AddedItemCategories': product.tags,
                    'AddedItemImageURL': product.featured_image,
                    'AddedItemURL': 'https://getlivfresh.com/products/' + product.handle,
                    'AddedItemPrice': priceCents,
                    'AddedItemQuantity': quantity,
                    'ItemNames': [product.title],
                    'CheckoutURL': 'https://livfresh.com/checkout',
                    'Items': [{
                        'ProductID': product.id,
                        'SKU': product.variants && product.variants[0] && product.variants[0].sku,
                        'ProductName': product.title,
                        'Quantity': quantity,
                        'ItemPrice': state.price || 0,
                        'RowTotal': (state.price || 0) * quantity,
                        'ProductURL': 'https://getlivfresh.com/products/' + product.handle,
                        'ImageURL': product.featured_image,
                        'ProductCategories': product.tags
                    }]
                }]);
            }
        } catch (kErr) { console.warn('Klaviyo push failed:', kErr); }
    });

    /* ============ "X people checking out" counter ============ */
    function refreshCheckingOut() {
        section.querySelectorAll('.productV2CheckingOutNum').forEach(function (el) {
            el.innerHTML = Math.floor(Math.random() * (127 - 70 + 1) + 70);
        });
    }
    refreshCheckingOut();
    setInterval(refreshCheckingOut, 3000);

    /* ============ Pre-order date + percent sold ============ */
    (function () {
        const now      = new Date();
        const target   = new Date("2024-04-28T23:59:00");
        const daysPast = Math.floor((now - target) / (1000 * 60 * 60 * 24) % 7);

        const nextMonday = new Date(now); nextMonday.setDate(now.getDate() + 8 - now.getDay());
        const lastMonday = new Date(now); lastMonday.setDate(now.getDate() + 1 - now.getDay());

        section.querySelectorAll('.percentSold').forEach(function (el) {
            el.textContent = daysPast + 91;
        });
        section.querySelectorAll('.preOrderDate').forEach(function (el) {
            el.textContent = nextMonday.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
        });
        section.querySelectorAll('.lastOrderDate').forEach(function (el) {
            el.textContent = lastMonday.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
        });
    })();

    /* ============ Accordion dropdowns (HOW IT WORKS / FAQS / etc.) ============
       CSS already handles the open/closed visuals via the `.active` class on
       `.productV2DropsItem`. JS just toggles that class on title click. */
    section.querySelectorAll('.productV2DropsItemTitle').forEach(function (titleEl) {
        titleEl.addEventListener('click', function () {
            const item = titleEl.closest('.productV2DropsItem');
            if (item) item.classList.toggle('active');
        });
    });

    /* "Show more" button inside a dropdown — reveals the rest of the body. */
    section.querySelectorAll('.productV2DropsItemBtn').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
            e.stopPropagation();
            btn.classList.add('active');
            const body = btn.closest('.productV2DropsItemBody');
            if (body) body.classList.add('expanded');
        });
    });
})();
